/**
 * Form Page
 * 
 * Public page that renders a form by slug
 */

import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FormRenderer } from '@/components/forms/FormRenderer';
import { formBuilderService } from '@/services/formBuilderService';
import { SEO } from '@/components/SEO';
import { Loader2 } from 'lucide-react';

export default function Form() {
  const { slug } = useParams<{ slug: string }>();

  const { data: form, isLoading, error } = useQuery({
    queryKey: ['form', slug],
    queryFn: async () => {
      if (!slug) return null;
      const result = await formBuilderService.getFormBySlug(slug);
      return result.success ? result.data : null;
    },
    enabled: !!slug,
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-muted-foreground" />
          <p className="text-muted-foreground">Loading form...</p>
        </div>
      </div>
    );
  }

  if (error || !form) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-2">Form Not Found</h1>
          <p className="text-muted-foreground">The form you're looking for doesn't exist or is not published.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO
        title={form.name}
        description={form.description || `Fill out the ${form.name} form`}
      />
      <div className="min-h-screen bg-background py-12">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card>
            <CardHeader>
              <CardTitle className="text-3xl">{form.name}</CardTitle>
              {form.description && (
                <p className="text-muted-foreground mt-2">{form.description}</p>
              )}
            </CardHeader>
            <CardContent>
              <FormRenderer form={form} />
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}

